import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';
import { RISK_LABELS, explainFlag, FRIENDLY_METRICS } from './opsPlaybook.js';
import './OpsDashboard.css';

export default function OpsDashboard() {
    const [snap, setSnap] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let timer;
        let cancelled = false;
        async function tick() {
            try {
                const s = await api.getOpsSnapshot();
                if (!cancelled) {
                    setSnap(s);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) setError(e.message || 'No se pudo conectar con el panel de salud del servidor.');
            }
            timer = setTimeout(tick, 5000);
        }
        tick();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, []);

    const issues = useMemo(() => {
        const flags = snap?.operational?.flags || [];
        return flags.map(explainFlag);
    }, [snap]);

    const riskKey = snap?.operational?.riskLevel || 'ok';
    const risk = RISK_LABELS[riskKey] || RISK_LABELS.ok;

    const kpiValues = useMemo(() => {
        if (!snap) return {};
        const http = snap.http || {};
        const lp = http.latencyPercentiles || {};
        const sqlc = snap.sqliteContention || {};
        return {
            tenantsActive: snap.tenantsActive,
            throughputRps: http.throughputRps != null ? Math.round(http.throughputRps * 10) / 10 : null,
            p95: lp.p95 != null ? Math.round(lp.p95) : null,
            errorRate:
                http.errorRateServer != null
                    ? `${Math.round(Number(http.errorRateServer) * 10000) / 100}%`
                    : '0%',
            activeRequests: snap.activeRequests,
            busyRetries: sqlc.busyRetryEvents,
            writeFails: sqlc.writeFailuresContention,
        };
    }, [snap]);

    if (error) {
        return (
            <div className="ops-page ops-error">
                <h1>Panel operacional</h1>
                <p>
                    <strong>No se pudo cargar el estado del sistema.</strong> {error}
                </p>
                <p style={{ color: '#64748b', marginTop: '1rem' }}>
                    Posibles causas: el servidor de ALINEA no está en marcha, no hay sesión de súper administrador, o
                    falta conexión de red. Solución habitual: verificar que el backend esté activo y volver a iniciar
                    sesión.
                </p>
            </div>
        );
    }

    if (!snap) {
        return (
            <div className="ops-page">
                <p className="text-muted">Consultando salud de la plataforma…</p>
            </div>
        );
    }

    const criticalCount = issues.filter((i) => i.severity === 'critical').length;
    const warnCount = issues.filter((i) => i.severity === 'warn').length;

    return (
        <div className="ops-page container animate-in">
            <h1>Panel operacional</h1>
            <p className="ops-lead">
                Resumen ejecutivo de la salud de ALINEA para dirección y soporte. Se actualiza cada pocos segundos. No
                requiere conocimientos técnicos: indica si la plataforma está bien, qué puede estar fallando y qué hacer.
            </p>

            <div className="ops-risk-banner" style={{ background: risk.color }}>
                <h2>Estado general: {risk.title}</h2>
                <p>{risk.summary}</p>
                {issues.length > 0 && (
                    <p style={{ marginTop: '0.65rem', fontSize: '0.88rem' }}>
                        {criticalCount > 0 && (
                            <span>
                                {criticalCount} punto{criticalCount !== 1 ? 's' : ''} crítico
                                {warnCount > 0 ? ' · ' : ''}
                            </span>
                        )}
                        {warnCount > 0 && (
                            <span>
                                {warnCount} aviso{warnCount !== 1 ? 's' : ''} de atención
                            </span>
                        )}
                    </p>
                )}
            </div>

            <section className="ops-section" aria-label="Resumen ejecutivo">
                <h2>Resumen ejecutivo</h2>
                {issues.length === 0 ? (
                    <div className="ops-ok-card">
                        No hay alertas activas. Los tiempos de respuesta, errores y carga de la base de datos están
                        dentro de lo esperado. Puede seguir operando con normalidad; conviene revisar este panel antes
                        de capacitaciones masivas o onboarding de clientes nuevos.
                    </div>
                ) : (
                    <>
                        <p style={{ margin: '0 0 1rem', color: '#475569', fontSize: '0.9rem', lineHeight: 1.5 }}>
                            A continuación se listan las situaciones detectadas automáticamente, en orden de gravedad,
                            con acciones recomendadas para quien coordina la operación (no para desarrolladores).
                        </p>
                        {issues
                            .sort((a, b) => {
                                const order = { critical: 0, warn: 1 };
                                return (order[a.severity] ?? 2) - (order[b.severity] ?? 2);
                            })
                            .map((issue) => (
                                <article
                                    key={`${issue.title}-${issue.rawMessage}`}
                                    className={`ops-issue-card${issue.severity === 'critical' ? ' critical' : ''}`}
                                >
                                    <div className="ops-severity">
                                        {issue.severity === 'critical' ? 'Prioridad alta' : 'Revisar pronto'}
                                    </div>
                                    <h3>{issue.title}</h3>
                                    <p>
                                        <strong>Qué significa:</strong> {issue.meaning}
                                    </p>
                                    <p style={{ marginBottom: '0.4rem' }}>
                                        <strong>Qué puede hacer:</strong>
                                    </p>
                                    <ul>
                                        {issue.actions.map((action) => (
                                            <li key={action}>{action}</li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                    </>
                )}
            </section>

            <section className="ops-section" aria-label="Indicadores clave">
                <h2>Indicadores clave (lectura rápida)</h2>
                <div className="ops-kpi-grid">
                    {FRIENDLY_METRICS.map((m) => (
                        <div key={m.key} className="ops-kpi">
                            <div className="ops-kpi-label">{m.label}</div>
                            <div className="ops-kpi-value">{kpiValues[m.key] ?? '—'}</div>
                            <div className="ops-kpi-hint">{m.hint}</div>
                        </div>
                    ))}
                </div>
            </section>

            <details className="ops-details-toggle">
                <summary>Detalle técnico (solo soporte TI)</summary>
                <pre>{JSON.stringify(snap.operational, null, 2)}</pre>
            </details>

            <p className="ops-footnote">
                Última lectura: {snap.generatedAt ? new Date(snap.generatedAt).toLocaleString('es-CO') : '—'}. Si las
                alertas persisten más de 30 minutos, escale al responsable técnico de ALINEA con captura de esta pantalla.
            </p>
        </div>
    );
}
